import { type ButtonHTMLAttributes, type MouseEvent, forwardRef } from 'react';

export type TvButtonVariant = 'primary' | 'ghost' | 'danger' | 'tile';

export interface TvButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: TvButtonVariant;
}

/**
 * The one thing the D-pad can land on.
 *
 * A real `<button>` rather than a styled div, which is what makes OK (Enter, and
 * DPAD_CENTER once the WebView has translated it) and a tablet tap both work
 * without this component knowing which one happened.
 *
 * Note `aria-disabled` rather than `disabled` for anything temporarily
 * unavailable — a cooling-down action, a form that is not ready yet. A truly
 * disabled button is dropped from navigation *and* blurred by the browser, so on
 * a television the focus ring vanishes mid-interaction and the next arrow press
 * starts from nowhere. Staying focusable keeps the ring where the player left
 * it, and keeps the countdown they are waiting on in front of them; the click is
 * swallowed here instead.
 */
export const TvButton = forwardRef<HTMLButtonElement, TvButtonProps>(function TvButton(
  { variant = 'ghost', className, onClick, ...rest },
  ref,
) {
  const inert = rest['aria-disabled'] === true || rest['aria-disabled'] === 'true';

  return (
    <button
      ref={ref}
      type="button"
      data-focusable=""
      className={['tv-button', `tv-button--${variant}`, className].filter(Boolean).join(' ')}
      onClick={(event: MouseEvent<HTMLButtonElement>) => {
        if (inert) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
      {...rest}
    />
  );
});
